/**
 * FloorMatch SEO data — Greater Manchester borough and flooring category
 * definitions. Every URL slug, meta description, H1, and localised copy
 * lives here so we ship one consistent set of pages tuned for long-tail
 * search intent.
 *
 * Naming convention (matters for URL SEO):
 *  - Borough slugs are the lowercase kebab of the metropolitan district
 *    name (e.g. "manchester", "trafford"). Do NOT prefix "greater-".
 *  - Category slugs match the flooring_type used in the backend so a
 *    single normalisation step maps SEO URLs → search API params.
 *
 * Long-tail keyword focus:
 *  * "[category] remnants [borough]" — highest buyer intent
 *  * "cheap [category] [borough]"
 *  * "surplus [category] [borough]"
 *  * "[category] roll ends [borough]"
 */

export const GM_BOROUGHS = [
  {
    slug: "manchester",
    name: "Manchester",
    postcodes: ["M1", "M2", "M3", "M4", "M5", "M6", "M8", "M11", "M12", "M13", "M14", "M15", "M16", "M17", "M18", "M19", "M20", "M21", "M22", "M40"],
    centroid: { lat: 53.4808, lng: -2.2426 },
    tagline: "the heart of Greater Manchester",
    neighbourhoods: ["Ancoats", "Northern Quarter", "Didsbury", "Chorlton", "Withington", "Rusholme", "Fallowfield", "Levenshulme"],
    hero_intro:
      "From city-centre new-builds in Ancoats to Victorian semis in Didsbury, Manchester flats and family homes work through miles of flooring every year. FloorMatch surfaces the leftover carpet, laminate and vinyl from independent retailers across the M postcodes — so you can grab quality flooring at a fraction of retail and collect it locally the same week.",
  },
  {
    slug: "salford",
    name: "Salford",
    postcodes: ["M3", "M5", "M6", "M7", "M27", "M28", "M30", "M50"],
    centroid: { lat: 53.4875, lng: -2.2901 },
    tagline: "MediaCity to Monton",
    neighbourhoods: ["Salford Quays", "Eccles", "Swinton", "Worsley", "Walkden", "Monton", "Pendleton"],
    hero_intro:
      "Whether you're kitting out an apartment at Salford Quays or a terraced house in Eccles, remnants and roll ends save you hundreds versus full-price retail. FloorMatch matches your room dimensions to surplus stock held by verified independent Salford retailers, ready for collection.",
  },
  {
    slug: "stockport",
    name: "Stockport",
    postcodes: ["SK1", "SK2", "SK3", "SK4", "SK5", "SK6", "SK7", "SK8"],
    centroid: { lat: 53.4106, lng: -2.1575 },
    tagline: "from Cheadle to Marple",
    neighbourhoods: ["Cheadle", "Bramhall", "Hazel Grove", "Marple", "Heaton Moor", "Reddish", "Edgeley", "Romiley"],
    hero_intro:
      "Stockport's mix of period cottages, 1930s semis and modern new-builds means every second week a flooring retailer somewhere in SK is left with a perfectly good bit of surplus roll-end. FloorMatch shows you exactly which ones fit your room, priced to move.",
  },
  {
    slug: "trafford",
    name: "Trafford",
    postcodes: ["M16", "M17", "M31", "M32", "M33", "M41", "WA14", "WA15"],
    centroid: { lat: 53.4197, lng: -2.3298 },
    tagline: "Altrincham to Old Trafford",
    neighbourhoods: ["Altrincham", "Sale", "Urmston", "Stretford", "Timperley", "Hale", "Bowdon", "Davyhulme"],
    hero_intro:
      "Trafford homeowners waste less than most — but the retailers stocking your Sale or Altrincham renovation still run into surplus rolls every month. FloorMatch is the fastest way to grab those bargains locally.",
  },
  {
    slug: "tameside",
    name: "Tameside",
    postcodes: ["OL5", "OL6", "OL7", "SK14", "SK15", "SK16"],
    centroid: { lat: 53.4808, lng: -2.0808 },
    tagline: "Ashton to Stalybridge",
    neighbourhoods: ["Ashton-under-Lyne", "Denton", "Stalybridge", "Hyde", "Droylsden", "Audenshaw", "Dukinfield"],
    hero_intro:
      "Tameside sits at the eastern edge of Greater Manchester with rows of solid Victorian and Edwardian housing that all need flooring at some point. FloorMatch surfaces the surplus stock from independent retailers across OL and SK postcodes so residents get quality flooring without paying full retail.",
  },
  {
    slug: "oldham",
    name: "Oldham",
    postcodes: ["OL1", "OL2", "OL3", "OL4", "OL8", "OL9"],
    centroid: { lat: 53.5409, lng: -2.1114 },
    tagline: "Saddleworth to Chadderton",
    neighbourhoods: ["Chadderton", "Royton", "Shaw", "Uppermill", "Delph", "Failsworth", "Lees"],
    hero_intro:
      "From Saddleworth village stone cottages to Chadderton semis, Oldham homes cover every era. Independent retailers around the OL postcodes stock everything from carpet remnants to LVT pack ends — FloorMatch matches them to your room in seconds.",
  },
  {
    slug: "rochdale",
    name: "Rochdale",
    postcodes: ["OL10", "OL11", "OL12", "OL15", "OL16"],
    centroid: { lat: 53.6136, lng: -2.1610 },
    tagline: "Rochdale to Littleborough",
    neighbourhoods: ["Middleton", "Heywood", "Milnrow", "Littleborough", "Wardle", "Norden"],
    hero_intro:
      "Rochdale's got some of the best-value housing in Greater Manchester and independent flooring retailers to match. FloorMatch surfaces their surplus stock so a full-house flooring refresh doesn't need a full-price budget.",
  },
  {
    slug: "bury",
    name: "Bury",
    postcodes: ["BL0", "BL8", "BL9", "M25", "M26", "M45"],
    centroid: { lat: 53.5933, lng: -2.2966 },
    tagline: "Prestwich to Ramsbottom",
    neighbourhoods: ["Prestwich", "Whitefield", "Radcliffe", "Ramsbottom", "Tottington", "Unsworth"],
    hero_intro:
      "Bury runs from the northern edge of Manchester up through Prestwich and Whitefield to Ramsbottom in the West Pennine Moors. FloorMatch shows you exactly which surplus rolls and pack ends fit your room, held by independent retailers across BL and M postcodes.",
  },
  {
    slug: "bolton",
    name: "Bolton",
    postcodes: ["BL1", "BL2", "BL3", "BL4", "BL5", "BL6", "BL7"],
    centroid: { lat: 53.5769, lng: -2.4282 },
    tagline: "Horwich to Little Lever",
    neighbourhoods: ["Horwich", "Westhoughton", "Farnworth", "Little Lever", "Kearsley", "Bromley Cross", "Egerton"],
    hero_intro:
      "Bolton's independent flooring trade is one of the busiest in Greater Manchester. FloorMatch surfaces their surplus stock — rolls, offcuts and pack ends — priced to move and matched to your exact room dimensions.",
  },
  {
    slug: "wigan",
    name: "Wigan",
    postcodes: ["WN1", "WN2", "WN3", "WN4", "WN5", "WN6", "WN7", "WN8"],
    centroid: { lat: 53.5450, lng: -2.6325 },
    tagline: "Wigan to Leigh",
    neighbourhoods: ["Leigh", "Ashton-in-Makerfield", "Standish", "Hindley", "Atherton", "Tyldesley", "Golborne"],
    hero_intro:
      "Wigan sits at the western edge of Greater Manchester, and the flooring retailers around WN postcodes cover everything from Standish new-builds to Leigh terraces. FloorMatch surfaces the surplus stock they haven't found a home for yet.",
  },
];

// Flooring categories — slug MUST match backend `flooring_type` for search.
export const ALL_CATEGORIES = [
  {
    slug: "carpet",
    name: "Carpet",
    plural: "carpet remnants",
    intent: "carpet remnants roll ends",
    intro: "Wool-blend twist, saxony, stripe loop and berber remnants from Greater Manchester's independent carpet retailers. Every remnant is measured — enter your room dimensions to see which pieces fit.",
    match_mode: "dimensions",
  },
  {
    slug: "laminate",
    name: "Laminate",
    plural: "laminate flooring",
    intent: "laminate flooring pack ends",
    intro: "Surplus laminate packs from finished trade jobs across Greater Manchester. FloorMatch works out how many packs your room needs (with a 10 % wastage allowance built in) and only shows listings with enough coverage.",
    match_mode: "coverage",
  },
  {
    slug: "lvt",
    name: "LVT",
    plural: "luxury vinyl tile",
    intent: "LVT flooring surplus",
    intro: "Karndean, Amtico and every mid-market LVT range shows up here as pack surplus. Enter your room size — we calculate whole packs required and only surface stock that covers you.",
    match_mode: "coverage",
  },
  {
    slug: "vinyl",
    name: "Vinyl",
    plural: "vinyl flooring roll ends",
    intent: "vinyl flooring lino remnants",
    intro: "Sheet vinyl and lino remnants from independent retailers across Greater Manchester. Cushioned, wood-effect, stone-effect — all measured, all ready to collect.",
    match_mode: "dimensions",
  },
  {
    slug: "artificial_grass",
    name: "Artificial grass",
    plural: "artificial grass offcuts",
    intent: "artificial grass offcuts remnants",
    intro: "Astro-turf and premium artificial grass offcuts — perfect for balconies, small back gardens and dog runs. Enter the area you need to cover, we do the fit maths.",
    match_mode: "dimensions",
  },
];
// Artificial grass is paused — kept in ALL_CATEGORIES for reactivation.
export const CATEGORIES = ALL_CATEGORIES.filter((c) => c.slug !== "artificial_grass");
export const isCategoryPaused = (slug) => slug === "artificial_grass";

export function findBorough(slug) {
  return GM_BOROUGHS.find((b) => b.slug === slug) || null;
}

export function findCategory(slug) {
  return ALL_CATEGORIES.find((c) => c.slug === slug) || null;
}

/**
 * Canonical site origin used to build canonical URLs and JSON-LD @id values.
 *
 * Priority:
 *   1. REACT_APP_PUBLIC_SITE_URL (baked in at build time — the source of
 *      truth for prod deployments so canonicals always point at the real
 *      customer domain no matter where the JS is served from)
 *   2. window.location.origin (dev/preview fallback)
 *   3. Hardcoded prod default
 */
export const SITE_ORIGIN =
  (typeof process !== "undefined" && process.env?.REACT_APP_PUBLIC_SITE_URL) ||
  (typeof window !== "undefined" && window.location?.origin) ||
  "https://floor-match.co.uk";

export const SITE_NAME = "Floor-Match";
export const SITE_TAGLINE =
  "Greater Manchester's surplus flooring marketplace — carpet, laminate, LVT and vinyl remnants from independent local retailers, matched to your room size, reserved online and collected locally.";
